import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  SizeColumnsToFitGridStrategy,
} from "ag-grid-enterprise";
import { AgBarSeriesOptions, AgChartOptions } from "ag-charts-enterprise";
import useSidebar from "src/hooks/useSidebar";
import useReference from "src/hooks/useReference";
import useTheme from "src/hooks/useTheme";
import { AgCharts } from "ag-charts-react";
import BaseGrid from "../../BaseGrid";
import ValidationService from "src/services/ValidationService";
import { ProgramSummaryDetailModel } from "src/models/ProgramSummaryDetailModel";
import { ProgramSummaryDetailSummaryModel } from "src/models/ProgramSummaryDetailSummaryModel";
import { CardAmountByDate } from "src/models/CardAmountByDate";
import ProgramSummaryDetailSummaryData from "./ProgramSummaryDetailSummaryData";
import { downloadFile } from "src/utils/DownloadFile";
import useReport from "src/hooks/useReport";
import SearchCriteria from "src/components/SearchCriteria";
import LoadingComponent from "src/components/LoadingComponent";

type ChartData = {
  amount: number;
  date: string;
};

const ProgramSummaryDetail = () => {
  const { setIsOpen } = useSidebar();
  const { appSettings } = useReference();
  const { theme } = useTheme();
  const {
    reportInputs,
    setExceedsMaxDateRange,
    mapReportRequest,
    currencyFormatter,
    defaultColDef,
    getContextMenuItems,
  } = useReport();
  const [summaryDetail, setSummaryDetail] =
    useState<ProgramSummaryDetailSummaryModel>(
      {} as ProgramSummaryDetailSummaryModel
    );
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const nextOptions = { ...options };
    nextOptions.theme = theme === "dark" ? "ag-material-dark" : "ag-material";
    setOptions(nextOptions);
  }, [theme]);

  const handleViewReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_View
      )
    ) {
      setIsDownload(false);
      setExceedsMaxDateRange(false);
      setGridReady(true);
      setSummaryDetail({} as ProgramSummaryDetailSummaryModel);
      setChartData([]);
      setDataSource(createServerSideDatasource());
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const handleDownloadReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_Download
      )
    ) {
      setIsDownload(true);
      setIsLoading(true);
      setExceedsMaxDateRange(false);
      ReportApi.exportProgramSummaryDetails(mapReportRequest(null))
        .then((res) => {
          downloadFile(ProgramSummaryDetail.displayName, res.data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const [colDefs] = useState<ColDef<ProgramSummaryDetailModel>[]>([
    { field: "date" },
    { field: "cardsFunded" },
    {
      headerName: "Funded Amount",
      field: "amount",
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    { field: "cardsReloaded" },
    {
      field: "reloadedAmount",
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    {
      headerName: "Property Fees",
      field: "propFee",
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    {
      headerName: "Daily Total",
      field: "totals",
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
  ]);

  const autoSizeStrategy = useMemo<SizeColumnsToFitGridStrategy>(() => {
    return {
      type: "fitGridWidth",
    };
  }, []);

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getProgramSummaryDetail(mapReportRequest(params))
      .then((res) => {
        const result = res.data;
        result?.rows.map(
          (x: ProgramSummaryDetailModel) =>
            (x.date = new Date(x.date).toLocaleDateString())
        );
        const successParams: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if ((result?.rows?.length ?? 0) > 0) {
          params?.api?.hideOverlay();
          params?.success(successParams);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }
        setSummaryDetail(result.summaryDetail);
        const cd = mapChartData(result.summaryDetail);
        setChartData(cd);
        setOptions((prevOptions) => ({
          ...prevOptions,
          data: cd,
        }));
      })
      .catch((error) => {
        console.error("getRows error", error);
        params?.fail();
      });
  };

  const mapChartData = (data: ProgramSummaryDetailSummaryModel) => {
    const cardAmountsByDate: CardAmountByDate[] = [];
    data.cardAmounts.map((ca) => {
      const cardAmount: CardAmountByDate = {
        amount: ca.amount,
        date: new Date(ca.date).toLocaleDateString(),
      };
      cardAmountsByDate.push(cardAmount);
    });
    return cardAmountsByDate;
  };

  const [options, setOptions] = useState<AgChartOptions>({
    title: {
      text: "Card Amount Total By Date",
    },
    // Data: Data to be displayed in the chart
    data: chartData as ChartData[],
    // Series: Defines which chart type and data to use
    series: [
      {
        type: "bar",
        xKey: "date",
        yKey: "amount",
      } as AgBarSeriesOptions,
    ],
    theme: theme === "dark" ? "ag-material-dark" : "ag-material",
  });

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Program Summary Detail</h5>
          {(reportInputs?.programId ?? 0) > 0 &&
            reportInputs?.fromDate &&
            reportInputs?.toDate && (
              <div className="report-header-date">{`${reportInputs?.fromDate.toLocaleDateString()} - ${reportInputs?.toDate.toLocaleDateString()}`}</div>
            )}
        </div>
        <div className="blocks">
          <SearchCriteria
            usesLocations={false}
            usesUsers={false}
            usesCampaigns={false}
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
          />
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-summary">
              <ProgramSummaryDetailSummaryData summaryDetail={summaryDetail} />
            </div>
          )}
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-chart">
              <AgCharts options={options} />
            </div>
          )}
        </div>
        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};
ProgramSummaryDetail.displayName = "ProgramSummaryDetail";

export default ProgramSummaryDetail;
